import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { HugeiconsIcon } from '@hugeicons/react'
import { PreferenceHorizontalIcon, Settings05Icon } from '@hugeicons-pro/core-bulk-rounded/index'
import { Box, FormControl, MenuItem, OutlinedInput, Select, TextField, Typography } from '@mui/material'
import PropTypes from 'prop-types'

import { selectOrderItems } from '@/app/services/orderItems'
import { selectLocale } from '@/app/slices/appSlice'
import { selectSelectedItems } from '@/app/slices/orderItemsSlice'
import { selectOrganisationId, selectUseImperialUnits } from '@/app/slices/organisationSlice'
import { formatNumber } from '@/common/helpers/formatUtilities'
import { useOrderItemPropertyOptions } from '@/common/hooks/useOrderItemPropertyOptions'

const classes = {
    ItemDetails_Settings: {
        width: '340px',
    },

    formControl_medium: {
        width: '200px',
    },
}

const SettingsBulkEdit = ({
    itemCuttingTechnology,
    itemGrainDirection,
    itemMaterial,
    itemQuantity,
    itemThickness,
    itemUnit,
    setItemCuttingTechnology,
    setItemGrainDirection,
    setItemMaterial,
    setItemQuantity,
    setItemThickness,
    setItemUnit,
    setUserMadeSettingsChanges,
}) => {
    const { t } = useTranslation()
    const { orderId } = useParams()

    const organisationId = useSelector(selectOrganisationId)
    const useImperialUnits = useSelector(selectUseImperialUnits)
    const locale = useSelector(selectLocale)
    const orderItems = useSelector((state) => selectOrderItems(state, { organisationId, orderId }))
    const selectedItemsArray = useSelector(selectSelectedItems)

    const [quantityError, setQuantityError] = useState(null)

    const orderItemsFromSelection = useMemo(
        () => orderItems.filter((item) => selectedItemsArray.includes(item.id)),
        [orderItems, selectedItemsArray]
    )

    const mode = useMemo(() => {
        return orderItemsFromSelection.every((item) => 'profile' in item) ? 'rotary' : 'flat'
    }, [orderItemsFromSelection])

    const { cuttingTechOptions, error, grainDirectionOptions, materialOptions, thicknessOptions } =
        useOrderItemPropertyOptions({
            cuttingTechnologyId: itemCuttingTechnology,
            materialId: itemMaterial,
            thickness: itemThickness,
            mode,
            partDimensions: null,
        })

    const units = useImperialUnits ? 'in' : 'mm'

    const handleQuantityChange = async (event) => {
        const value = parseInt(event.target.value, 10)
        setQuantityError(!value || isNaN(value) || value <= 0)
        setItemQuantity(value || 0)
    }

    const handleCuttingTechChange = async (event) => {
        const value = event?.target?.value
        if (Boolean(value) && itemCuttingTechnology !== value) {
            setItemCuttingTechnology(value)
            setItemMaterial('')
            setItemThickness('')
            setUserMadeSettingsChanges(true)
        }
    }

    const handleMaterialChange = async (event) => {
        const value = event?.target?.value
        if (Boolean(value) && itemMaterial !== value) {
            setItemMaterial(value)
            setItemThickness('')
            setUserMadeSettingsChanges(true)
        }
    }

    const handleThicknessChange = async (event) => {
        const value = event?.target?.value
        if (Boolean(value) && itemThickness !== value) {
            setItemThickness(value)
            setUserMadeSettingsChanges(true)
        }
    }

    const handleGrainDirectionChange = async (event) => {
        const value = event?.target?.value
        if (Boolean(value) && itemGrainDirection !== value) {
            setUserMadeSettingsChanges(true)
            setItemGrainDirection(value)
        }
    }

    const handleUnitChange = async (event) => {
        const value = event?.target?.value
        setItemUnit(value)
        setUserMadeSettingsChanges(true)
    }

    const allItemsHaveSameProperty = (array, property) => {
        return array.every((element) => element[property] === array[0][property])
    }

    useEffect(() => {
        if (!orderItemsFromSelection.length) return

        if (allItemsHaveSameProperty(orderItemsFromSelection, 'quantity')) {
            setItemQuantity(orderItemsFromSelection[0].quantity)
        }

        if (allItemsHaveSameProperty(orderItemsFromSelection, 'cuttingTechnologyId')) {
            setItemCuttingTechnology(orderItemsFromSelection[0].cuttingTechnologyId)
        }

        if (allItemsHaveSameProperty(orderItemsFromSelection, 'materialId')) {
            setItemMaterial(orderItemsFromSelection[0].materialId)
        }

        if (allItemsHaveSameProperty(orderItemsFromSelection, 'thickness')) {
            setItemThickness(orderItemsFromSelection[0].thickness)
        }

        if (allItemsHaveSameProperty(orderItemsFromSelection, 'grainDirection')) {
            setItemGrainDirection(orderItemsFromSelection[0].grainDirection)
        }

        if (allItemsHaveSameProperty(orderItemsFromSelection, 'isImperial')) {
            setItemUnit(orderItemsFromSelection[0].isImperial ? 'in' : 'mm')
        }
    }, [orderItemsFromSelection])

    if (error) {
        return <Typography color="error">{t('Error loading settings. Please try again.')}</Typography>
    }

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Box
                sx={{
                    alignItems: 'center',
                    display: 'flex',
                    flexDirection: 'row',
                    gap: 1,
                    mb: 2,
                }}
            >
                <HugeiconsIcon icon={Settings05Icon} />
                <Typography variant="strong1">{t('Settings')}</Typography>
            </Box>
            <Box
                sx={[
                    {
                        alignItems: 'flex-start',
                        display: 'flex',
                        flex: '0 0 auto',
                        flexDirection: 'column',
                        justifyContent: 'space-around',
                        gap: 1,
                    },
                    classes.ItemDetails_Settings,
                ]}
            >
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        width: '100%',
                    }}
                >
                    <Typography
                        data-testid="bulk-edit-cutting-technology-label"
                        variant="body2"
                    >
                        {t('Cutting technology')}
                    </Typography>
                    <FormControl
                        color="primary"
                        size="small"
                        sx={classes.formControl_medium}
                    >
                        <Select
                            id="select-itemCuttingTechnology"
                            input={<OutlinedInput sx={{ typography: 'inputTextSmall' }} />}
                            inputProps={{ 'data-testid': 'bulk-edit-cutting-technology' }}
                            labelId="select-itemCuttingTechnology-label"
                            readOnly={cuttingTechOptions?.length <= 1}
                            value={itemCuttingTechnology}
                            onChange={handleCuttingTechChange}
                        >
                            {cuttingTechOptions
                                ? cuttingTechOptions?.map((ct) => (
                                      <MenuItem
                                          key={ct.cuttingTechnologyId}
                                          sx={{ typography: 'inputTextSmall' }}
                                          value={ct.cuttingTechnologyId}
                                      >
                                          {ct.name}
                                      </MenuItem>
                                  ))
                                : null}
                        </Select>
                    </FormControl>
                </Box>
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        width: '100%',
                    }}
                >
                    <Typography
                        data-testid="bulk-edit-material-label"
                        variant="body2"
                    >
                        {t('Material')}
                    </Typography>
                    <FormControl
                        color="primary"
                        size="small"
                        sx={classes.formControl_medium}
                    >
                        <Select
                            id="select-itemMaterial"
                            input={<OutlinedInput sx={{ typography: 'inputTextSmall' }} />}
                            inputProps={{ 'data-testid': 'bulk-edit-material' }}
                            labelId="select-itemMaterial-label"
                            value={itemMaterial}
                            onChange={handleMaterialChange}
                        >
                            {materialOptions
                                ? materialOptions?.map((material) => (
                                      <MenuItem
                                          key={material.materialId}
                                          sx={{ typography: 'inputTextSmall' }}
                                          value={material.materialId}
                                      >
                                          {material.materialName}
                                      </MenuItem>
                                  ))
                                : null}
                        </Select>
                    </FormControl>
                </Box>
                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        width: '100%',
                    }}
                >
                    <Typography
                        data-testid="bulk-edit-thickness-label"
                        variant="body2"
                    >
                        {t('Thickness')}
                    </Typography>
                    <FormControl
                        color="primary"
                        disabled={!thicknessOptions?.length}
                        size="small"
                        sx={classes.formControl_medium}
                    >
                        <Select
                            id="select-itemThickness"
                            input={<OutlinedInput sx={{ typography: 'inputTextSmall' }} />}
                            inputProps={{ 'data-testid': 'bulk-edit-thickness' }}
                            labelId="select-itemThickness-label"
                            SelectDisplayProps={{ style: { textAlign: 'right' } }}
                            value={itemThickness}
                            onChange={handleThicknessChange}
                        >
                            {thicknessOptions
                                ? thicknessOptions?.map((thickness) => (
                                      <MenuItem
                                          key={thickness}
                                          sx={{ typography: 'inputTextSmall', justifyContent: 'flex-end' }}
                                          value={thickness}
                                      >
                                          {formatNumber(thickness, locale, useImperialUnits)} {units}
                                      </MenuItem>
                                  ))
                                : null}
                        </Select>
                    </FormControl>
                </Box>

                {mode === 'flat' ? (
                    <Box
                        sx={{
                            alignItems: 'center',
                            display: 'flex',
                            flexDirection: 'row',
                            justifyContent: 'space-between',
                            width: '100%',
                        }}
                    >
                        <Typography
                            data-testid="bulk-edit-grain-direction-label"
                            variant="body2"
                        >
                            {t('Grain direction')}
                        </Typography>
                        <FormControl
                            color="primary"
                            size="small"
                            sx={classes.formControl_medium}
                        >
                            <Select
                                data-testid="bulk-edit-grain-direction"
                                id="select-itemGrainDirection"
                                input={<OutlinedInput sx={{ typography: 'inputTextSmall' }} />}
                                labelId="select-itemConsumptionMode-label"
                                size="small"
                                value={itemGrainDirection}
                                onChange={handleGrainDirectionChange}
                            >
                                {grainDirectionOptions.map((direction) => (
                                    <MenuItem
                                        key={`direction-${direction.value}`}
                                        sx={{ typography: 'inputTextSmall' }}
                                        value={direction.value}
                                    >
                                        {direction.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Box>
                ) : null}

                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        width: '100%',
                    }}
                >
                    <Typography
                        data-testid="bulk-edit-units-label"
                        variant="body2"
                    >
                        {t('Units')}
                    </Typography>
                    <FormControl
                        color="primary"
                        margin="none"
                        size="small"
                        sx={classes.formControl_medium}
                        variant="outlined"
                    >
                        <Select
                            data-testid="bulk-edit-units-mode"
                            id="select-itemUnit"
                            input={<OutlinedInput sx={{ typography: 'inputTextSmall' }} />}
                            labelId="select-itemUnit-label"
                            size="small"
                            value={itemUnit}
                            onChange={handleUnitChange}
                        >
                            <MenuItem
                                sx={{ typography: 'inputTextSmall' }}
                                value="mm"
                            >
                                {t('Millimetres')}
                            </MenuItem>
                            <MenuItem
                                sx={{ typography: 'inputTextSmall' }}
                                value="in"
                            >
                                {t('Inches')}
                            </MenuItem>
                        </Select>
                    </FormControl>
                </Box>

                <Box
                    sx={{
                        alignItems: 'center',
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        width: '100%',
                    }}
                >
                    <Typography
                        data-testid="bulk-edit-quantity-label"
                        variant="body2"
                    >
                        {t('Quantity')}
                    </Typography>
                    <TextField
                        color="primary"
                        error={quantityError}
                        helperText={quantityError ? t('Quantity cannot be less than 1') : null}
                        size="small"
                        slotProps={{
                            htmlInput: {
                                style: { textAlign: 'right' },
                                min: 1,
                                'data-testid': 'bulk-edit-quantity',
                                inputMode: 'numeric',
                                pattern: '[0-9]*',
                            },
                        }}
                        sx={classes.formControl_medium}
                        type="text"
                        value={itemQuantity}
                        variant="outlined"
                        fullWidth
                        required
                        onChange={handleQuantityChange}
                    />
                </Box>
            </Box>
        </Box>
    )
}

SettingsBulkEdit.propTypes = {
    itemCuttingTechnology: PropTypes.string,
    itemGrainDirection: PropTypes.string,
    itemMaterial: PropTypes.string,
    itemQuantity: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    itemThickness: PropTypes.string,
    itemUnit: PropTypes.string,
    setItemCuttingTechnology: PropTypes.func,
    setItemGrainDirection: PropTypes.func,
    setItemMaterial: PropTypes.func,
    setItemQuantity: PropTypes.func,
    setItemThickness: PropTypes.func,
    setItemUnit: PropTypes.func,
    setUserMadeSettingsChanges: PropTypes.func,
}

export default SettingsBulkEdit
